import gatewayService from '../../../services/GatewayService';
import tokenExtractionService from '../../../services/TokenExtractionService';
import * as CONSTANTS from '../../constants';
import tokenStorageService from '../../../services/TokenStorageService';
import languageService from '../../../services/LanguageService';
import languages from '../../../i18n/languages'
import router from '../../../routes';
import {handleLoginNavigation} from "../../../services/NavigationService";
import * as storageService from '../../../services/StorageService';
import {getProductTypesAndSuppliersFromSuppliersProductsObject} from "../../../services/ConverterService";

/**
 * Authenticates a user
 *
 * @param context
 * @param email
 * @param pass
 */
export const authenticate = ({commit, state, dispatch}, {email, pass}) => {
    commit(CONSTANTS.AUTH_LOGIN_PENDING);

    // Clear the state and token to ensure no deprecated data is present
    storageService.clear();
    tokenStorageService.del();

    return gatewayService.authenticate(email, pass).then((res) => {
        const token = res.data.data;
        tokenStorageService.set(token);
        const data = {
            user: tokenExtractionService.user(token),
            suppliers: tokenExtractionService.suppliers(token)
        };
        commit(CONSTANTS.AUTH_LOGIN_SUCCESS, data);
        // If the user hasn't specified a language then set language to default fro user
        const userLanguage = languageService.getLanguageFromStorage() || tokenExtractionService.user(token).language;
        const language = {
            value: userLanguage,
            name: languages[userLanguage]
        };
        commit('nav/NAV_MENU_TOGGLE', false, {root: true});
        commit('nav/' + CONSTANTS.LANGUAGE_SELECT, language, {root: true});

        dispatch('getSuppliersAndProducts').then(() => {
            // Handle navigation
            const referrer = state.referrer;
            handleLoginNavigation(router, data.user, referrer);
        });
    }).catch((err) => {
        if (err.status === 401) {
            commit(CONSTANTS.AUTH_LOGIN_FAILURE, {
                level: 'warning',
                text: 'signIn.messages.mismatch'
            })
        } else {
            commit(CONSTANTS.AUTH_LOGIN_FAILURE, {
                level: 'error',
                text: 'signIn.messages.unexpected'
            })
        }
    });
};

/**
 * Sends a forgot password request
 *
 * @param context
 * @param email
 * @returns {*|PromiseLike<T>|Promise<T>}
 */
export const forgotPassword = ({commit}, {email}) => {
    commit(CONSTANTS.AUTH_FORGOT_PENDING);
    return gatewayService.forgotPassword(email).then(() => {
        commit(CONSTANTS.AUTH_FORGOT_SUCCESS, {
            level: 'success',
            text: 'forgotPassword.messages.success'
        })
    }).catch((err) => {
        if (err.status === 401) {
            commit(CONSTANTS.AUTH_FORGOT_FAILURE, {
                level: 'warning',
                text: 'forgotPassword.messages.noexist'
            });
        } else {
            commit(CONSTANTS.AUTH_FORGOT_FAILURE, {
                level: 'error',
                text: 'forgotPassword.messages.unexpected'
            });
        }
    });
};

/**
 * Sends a reset password request
 *
 * @param context
 * @param pass
 * @param token
 * @returns {*|PromiseLike<T>|Promise<T>}
 */
export const resetPassword = ({commit}, {pass, token}) => {
    commit(CONSTANTS.AUTH_RESET_PENDING);
    return gatewayService.resetPassword(pass, token).then(() => {
        commit(CONSTANTS.AUTH_RESET_SUCCESS, {
            level: 'success',
            text: 'resetPassword.messages.success'
        });
    }).catch((err) => {
        if (err.status === 400) {
            commit(CONSTANTS.AUTH_RESET_FAILURE, {
                level: 'warning',
                text: 'resetPassword.messages.mismatch'
            });
        } else if (err.status === 409) {
            commit(CONSTANTS.AUTH_RESET_FAILURE, {
                level: 'warning',
                text: 'resetPassword.messages.token'
            });
        } else {
            commit(CONSTANTS.AUTH_RESET_FAILURE, {
                level: 'error',
                text: 'resetPassword.messages.unexpected'
            });
        }
        return Promise.reject();
    });
};

/**
 * Sends an activate user request
 *
 * @param context
 * @param pass
 * @param token
 * @returns {*}
 */
export const activate = ({commit}, {pass, token}) => {
    commit(CONSTANTS.AUTH_ACTIVATION_PENDING);
    return gatewayService.activate(pass, token).then(() => {
        commit(CONSTANTS.AUTH_ACTIVATION_SUCCESS, {
            level: 'success',
            text: 'activateUser.messages.success'
        });
    }).catch((err) => {
        if (err.status === 400) {
            commit(CONSTANTS.AUTH_ACTIVATION_FAILURE, {
                level: 'warning',
                text: 'activateUser.messages.mismatch'
            });
        } else if (err.status === 409) {
            commit(CONSTANTS.AUTH_ACTIVATION_FAILURE, {
                level: 'warning',
                text: 'activateUser.messages.token'
            });
        } else {
            commit(CONSTANTS.AUTH_ACTIVATION_FAILURE, {
                level: 'error',
                text: 'activateUser.messages.unexpected'
            });
        }
    });
};

/**
 * Unauthenticates the user
 *
 * @param context
 * @param payload
 */
export const unauthenticate = ({commit}, payload) => {
    if (typeof payload !== 'undefined') {
        commit(CONSTANTS.AUTH_MESSAGE_SET, payload);
        if(payload.text === 'signIn.logoutNotice') {
            commit(CONSTANTS.AUTH_REFERRER_UNSET);
        }
    }
    storageService.clear();
    tokenStorageService.del();
    router.push('/');
    commit(CONSTANTS.AUTH_USER_UNSET);
    commit(CONSTANTS.AUTH_SUPPLIER_UNSET);
};

/**
 * Updates the user in state
 *
 * @param context
 * @param user
 */
export const setUser = ({commit}, {user}) => {
    // If the user hasn't specified a language then set language to default fro user
    const userLanguage = languageService.getLanguageFromStorage() || user.language;
    const language = {
        value: userLanguage,
        name: languages[userLanguage]
    };
    commit('nav/' + CONSTANTS.LANGUAGE_SELECT, language, {root: true});
    commit(CONSTANTS.AUTH_USER_SET, user)
};

/**
 * Sets a login notice notice
 */
export const setSignInMessage = ({commit}, payload) => {
    commit(CONSTANTS.AUTH_MESSAGE_SET, payload)
};

/**
 * Sets the referrer path of the client to use for navigation back to requested page.
 *
 * @param commit
 * @param payload
 */
export const setReferrer = ({commit}, payload) => {
    commit(CONSTANTS.AUTH_REFERRER_SET, payload);
};

/**
 * Unset the referrer path-
 *
 * @param commit
 */
export const unsetReferrer = ({commit}) => {
    commit(CONSTANTS.AUTH_REFERRER_UNSET);
};

/**
 * Get suppliers and products object for the nav menu.
 *
 * @param commit
 * @param dispatch
 * @returns {Promise<T | never>}
 */
export const getSuppliersAndProducts = ({ commit, dispatch }) => {
    commit(CONSTANTS.AUTH_SUPPLIERS_PRODUCTS_LIST_PENDING);
    const token = tokenStorageService.get();
    const suppliersAndProducts = storageService.get('suppliersAndProducts');

    if (suppliersAndProducts) {
        commit(CONSTANTS.AUTH_SUPPLIERS_PRODUCTS_LIST_SUCCESS, suppliersAndProducts);
        const productTypeAndSupplier = getProductTypesAndSuppliersFromSuppliersProductsObject(suppliersAndProducts);
        dispatch('setSupplier', {supplier: productTypeAndSupplier.supplier, overwrite: false});
        dispatch('setProductType', {productType: productTypeAndSupplier.productType, overwrite: false});
        dispatch('setProductTypes', productTypeAndSupplier.productTypes);
    } else {
        return gatewayService.getSuppliersAndProducts(token).then((res) => {
            const data = res.data.data;
            commit(CONSTANTS.AUTH_SUPPLIERS_PRODUCTS_LIST_SUCCESS, data);
            const productTypeAndSupplier = getProductTypesAndSuppliersFromSuppliersProductsObject(data);
            dispatch('setSupplier', {supplier: productTypeAndSupplier.supplier, overwrite: true});
            dispatch('setProductType', {productType: productTypeAndSupplier.productType, overwrite: true});
            dispatch('setProductTypes', productTypeAndSupplier.productTypes);
            storageService.save('suppliersAndProducts', data);
        }).catch(() => {
            commit(CONSTANTS.AUTH_SUPPLIERS_PRODUCTS_LIST_FAILURE)
        })
    }
};

/**
 * Set the supplier.
 * Save for the session to ensure easy navigation.
 *
 * @param commit
 * @param payload
 */
export const setSupplier = ({ commit }, payload) => {
    const supplier = payload.overwrite
        ? payload.supplier
        : storageService.get('supplier') || payload.supplier;
    commit(CONSTANTS.AUTH_SUPPLIER_SET, supplier);
    storageService.save('supplier', supplier);
};

/**
 * Set the product type.
 *
 * @param commit
 * @param dispatch
 * @param payload
 */
export const setProductType = ({ commit, dispatch }, payload) => {
    const productType = payload.overwrite
        ? payload.productType
        : storageService.get('productType') || payload.productType;
    commit(CONSTANTS.AUTH_PRODUCT_TYPE_SET, productType);
    storageService.save('productType', productType);

    // Check if there is only one supplier in a product type - if so then set to that supplier
    dispatch('checkIfOnlySupplierInProductType', productType);
};

/**
 * Set the products types.
 *
 * @param commit
 * @param payload
 */
export const setProductTypes = ({ commit }, payload) => {
    commit(CONSTANTS.AUTH_PRODUCT_TYPES_SET, payload);
};

/**
 * Check if there is only one supplier in the given product type, if so then set supplier to said one.
 *
 * @param state
 * @param dispatch
 * @param payload
 */
export const checkIfOnlySupplierInProductType = ({ state, dispatch }, payload) => {
    const suppliersAndProducts = state.suppliersProducts.items;
    if (suppliersAndProducts[payload].length === 1) {
        const supplier = suppliersAndProducts[payload][0];
        dispatch('setSupplier', {supplier: supplier, overwrite: true});
    }
};



// WEBPACK FOOTER //
// ./src/store/modules/auth/actions.js